/**
 * <s-bundle-tiers> — "Buy more, save more" pack picker.
 *
 * Choosing a tier radio sets the closest form's quantity input (selector from
 * data-quantity-input) and dispatches `change` so <s-quantity-selector> syncs.
 * Changing the quantity by hand selects the matching tier, or clears the
 * selection when no tier matches. The radios use a detached `form` attribute,
 * so they are never submitted with the product form.
 */
class SBundleTiers extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const quantityInput = this.#quantityInput();

    this.addEventListener('change', (event) => {
      const radio = event.target;
      if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
      const input = this.#quantityInput();
      if (input) {
        input.value = radio.dataset.tierQty;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.#updateTotal(radio);
    }, { signal });

    if (quantityInput) {
      quantityInput.addEventListener('change', () => this.#syncFromQuantity(), { signal });
      quantityInput.closest('s-quantity-selector')?.addEventListener('s-quantity:change', () => this.#syncFromQuantity(), { signal });
      this.#syncFromQuantity();
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #quantityInput() {
    const selector = this.dataset.quantityInput || 'input[name="quantity"]';
    const form = this.closest('form');
    return (form || document).querySelector(selector);
  }

  #radios() {
    return Array.from(this.querySelectorAll('input[type="radio"][data-tier-qty]'));
  }

  #syncFromQuantity() {
    const input = this.#quantityInput();
    if (!input) return;
    const value = String(Number.parseInt(input.value, 10));
    let match = null;
    for (const radio of this.#radios()) {
      radio.checked = radio.dataset.tierQty === value;
      if (radio.checked) match = radio;
    }
    this.#updateTotal(match);
  }

  #updateTotal(radio) {
    const total = this.querySelector('[data-bundle-total]');
    if (!total) return;
    if (!radio) {
      total.hidden = true;
      return;
    }
    const price = total.querySelector('[data-bundle-total-price]');
    const compare = total.querySelector('[data-bundle-total-compare]');
    if (price) price.textContent = radio.dataset.tierPrice || '';
    if (compare) compare.textContent = radio.dataset.tierCompare !== radio.dataset.tierPrice ? radio.dataset.tierCompare || '' : '';
    total.hidden = false;
  }
}

if (!customElements.get('s-bundle-tiers')) {
  customElements.define('s-bundle-tiers', SBundleTiers);
}
